import {
  isBoolean,
  isFunction,
  isJsonObject,
  isNumber,
  isRecord,
  isString,
  type JsonValue,
} from '@crown/framework/ir';
import { Signal } from '@crown/signal';
import { effect } from '@crown/ui/effect';
import type {
  AfterNavigateHook,
  BeforeNavigateHook,
  OutletContent,
  OutletLoader,
  Outlets,
  RegisterRoutesOptions,
  RouteConfig,
  RouteMeta,
  RouterConfig,
  RouterInitOptions,
  RouterInstance,
} from '../types';
import { takeLoadedData } from './loaded-data';
import { createPrefetchSystem, type PrefetchCache } from './prefetch';
import { createRouterCore } from './runtimeCore';
import {
  isPromiseLike,
  moduleDefault,
  ROUTE_ATTRIBUTE,
  type RouteParams,
  routeEnabled,
} from './match';
import {
  createStrategyExecutor,
  getPreloadStrategy,
  type PreloadStrategySetting,
} from './strategies';

/** Empties every outlet still holding what the server rendered into it. */
function clearServerRenderedOutlets(): void {
  for (const outlet of document.querySelectorAll<HTMLElement>(
    `[router-outlet][${ROUTE_ATTRIBUTE}]`,
  )) {
    outlet.removeAttribute(ROUTE_ATTRIBUTE);
    outlet.replaceChildren();
  }
}

/**
 * Params as a route receives them. History state and callers written in
 * JavaScript can hand over anything; only strings, numbers and booleans are
 * kept.
 */
function normalizeParamRecord(
  obj: Readonly<Record<string, JsonValue | undefined>>,
): RouteParams {
  const out: RouteParams = {};
  for (const [k, v] of Object.entries(obj))
    if (isString(v) || isNumber(v) || isBoolean(v)) out[k] = v;
  return out;
}

/**
 * The params a history entry carries. The router writes `{ params }` on every
 * entry it pushes; an entry something else wrote carries none.
 */
function historyParams(state: JsonValue | undefined): RouteParams {
  if (!isRecord(state) || !('params' in state)) return {};
  const { params } = state;
  return isJsonObject(params) ? normalizeParamRecord(params) : {};
}

export function createRouter<T extends readonly RouteConfig[]>(
  config: RouterConfig<T>,
): RouterInstance<Extract<T[number]['path'], string>> {
  const DEFAULT_CONFIG: Required<Omit<RouterConfig<T>, 'routes'>> & {
    routes: RouteConfig[];
  } = {
    base: '/',
    defaultRoute: '',
    notFoundRoute: '',
    routes: [],
    enablePrefetch: true,
    prefetchDelay: 100,
    preloadStrategy: 'NoPreloading',
  };
  const routerConfig = { ...DEFAULT_CONFIG, ...config };
  const prefetchCache: PrefetchCache = new Map();
  const core = createRouterCore();
  const prefetchSystem = createPrefetchSystem({
    resolveComponent: core.resolveComponent,
    prefetchCache,
  });
  const strategyExecutor = createStrategyExecutor({
    prefetchRoute: prefetchSystem.prefetchRoute,
    getPreloadContext: prefetchSystem.getPreloadContext,
  });
  strategyExecutor.setStrategy(
    getPreloadStrategy(routerConfig.preloadStrategy),
  );
  const routerState = core.state;

  function joinPaths(base: string, path: string) {
    return core.joinPaths(base, path);
  }
  /** The route path the browser's address names, under the router's base. */
  function locationPath(): string {
    let path = window.location.pathname;
    if (routerConfig.base && routerConfig.base !== '/')
      path = path.startsWith(routerConfig.base)
        ? path.slice(routerConfig.base.length)
        : path;
    else if (path.startsWith('/')) path = path.slice(1);
    return path || routerConfig.defaultRoute;
  }
  function toBrowserPath(path: string) {
    if (!path || path === '/') return '/';
    return path.startsWith('/') ? path : `/${path}`;
  }

  let allRoutes: RouteConfig[] = [...routerConfig.routes];
  // Runtime registrations (plugins) are tracked separately: buildRouteIndex replaces the
  // index wholesale, so anything registered before that async flatten settles would
  // otherwise be discarded without trace.
  const dynamicRoutes: RouteConfig[] = [];
  let flattenReady: Promise<void> | null = null;
  async function buildRouteIndex() {
    const flattened: RouteConfig[] = [];
    async function flatten(routes: readonly RouteConfig[], parent = '') {
      for (const r of routes) {
        const full = joinPaths(parent, r.path);
        flattened.push({ ...r, path: full });
        if (r.children) await flatten(r.children, full);
      }
    }
    await flatten(routerConfig.routes);
    allRoutes = [...flattened, ...dynamicRoutes];
  }
  function ensureFlatten() {
    if (!flattenReady)
      flattenReady = buildRouteIndex().catch((cause: unknown) => {
        // Navigation still works from the static table; an index that failed
        // to build only costs the dynamic routes, and saying so is the
        // difference between that and there being none to add.
        console.error('[Router] the route index did not build:', cause);
      });
    return flattenReady;
  }
  ensureFlatten();

  async function expandLazyParentsForPath(path: string) {
    if (findRouteByPath(path)) return;
    const segments = path.split('/').filter(Boolean);
    for (let i = segments.length; i > 0; i--) {
      const prefix = segments.slice(0, i).join('/');
      const parent = allRoutes.find(
        (r) => r.loadChildren && core.matchPath(r.path, prefix),
      );
      if (parent?.loadChildren) {
        try {
          const mod = await parent.loadChildren();
          if (mod?.routes) {
            const added: RouteConfig[] = [];
            async function flattenChild(
              children: readonly RouteConfig[],
              parentBase: string,
            ) {
              for (const cr of children) {
                const full = joinPaths(parentBase, cr.path);
                added.push({ ...cr, path: full });
                if (cr.children) await flattenChild(cr.children, full);
              }
            }
            await flattenChild(mod.routes, parent.path);
            for (const r of added)
              if (!allRoutes.some((ar) => ar.path === r.path))
                allRoutes.push(r);
          }
        } catch {}
      }
      if (findRouteByPath(path)) return;
    }
  }

  function findRouteByPath(path: string) {
    return (
      allRoutes.find(
        (r) => routeEnabled(r) && !!core.matchPath(r.path, path),
      ) || null
    );
  }

  let _isActivating = false;
  let _lastActivatedPath: string | null = null;
  let _lastActivatedParams: string | null = null;

  function normalisePath(p: string) {
    return p.replace(/^\/+|\/+$/g, '');
  }

  function flattenForRegistration(
    routes: readonly RouteConfig[],
    parent: string,
    out: RouteConfig[],
    meta: RouteMeta,
  ) {
    for (const r of routes) {
      const full = joinPaths(parent, r.path);
      out.push({ ...r, path: full, meta: { ...r.meta, ...meta } });
      if (r.children) flattenForRegistration(r.children, full, out, meta);
    }
  }

  function purgePrefetchFor(path: string) {
    prefetchCache.delete(path);
    const outletPrefix = `${path}:outlet:`;
    for (const key of Array.from(prefetchCache.keys()))
      if (key.startsWith(outletPrefix)) prefetchCache.delete(key);
  }

  function registerRoutes(
    routes: readonly RouteConfig[],
    opts: RegisterRoutesOptions = {},
  ): () => void {
    const prefix = opts.prefix ? normalisePath(opts.prefix) : '';
    const stamped: RouteConfig[] = [];
    const pluginMeta: RouteMeta = { isPlugin: true };
    if (opts.source) pluginMeta.pluginSource = opts.source;
    if (prefix) pluginMeta.pluginPrefix = prefix;
    flattenForRegistration(routes, prefix, stamped, {
      ...pluginMeta,
      ...opts.meta,
    });

    // Validate the entire batch before applying any of it, so a rejected registration
    // leaves the index exactly as it was rather than half-applied.
    const seen = new Set<string>();
    for (const route of stamped) {
      const path = normalisePath(route.path);
      if (!path) throw new Error('[Router] registerRoutes: empty route path');
      if (path.split('/').some((s) => s === '' || s === '.' || s === '..'))
        throw new Error(
          `[Router] registerRoutes: illegal path segment in "${route.path}"`,
        );
      if (prefix && path !== prefix && !path.startsWith(`${prefix}/`))
        throw new Error(
          `[Router] registerRoutes: "${route.path}" escapes prefix "${prefix}"`,
        );
      if (seen.has(path))
        throw new Error(
          `[Router] registerRoutes: duplicate path "${route.path}" within batch`,
        );
      seen.add(path);
      if (allRoutes.some((existing) => normalisePath(existing.path) === path))
        throw new Error(
          `[Router] registerRoutes: "${route.path}" collides with an existing route`,
        );
    }

    for (const route of stamped) {
      allRoutes.push(route);
      dynamicRoutes.push(route);
    }

    let detached = false;
    return function detach() {
      if (detached) return;
      detached = true;
      const activeRoute = routerState.routeData.get();
      const removingActive = !!activeRoute && stamped.includes(activeRoute);
      for (const route of stamped) {
        const ai = allRoutes.indexOf(route);
        if (ai !== -1) allRoutes.splice(ai, 1);
        const di = dynamicRoutes.indexOf(route);
        if (di !== -1) dynamicRoutes.splice(di, 1);
        purgePrefetchFor(route.path);
        // Clear the activation dedupe so re-registering this path later still activates.
        if (
          _lastActivatedPath &&
          normalisePath(_lastActivatedPath) === normalisePath(route.path)
        ) {
          _lastActivatedPath = null;
          _lastActivatedParams = null;
        }
      }
      if (removingActive) {
        // A route being withdrawn must not be able to veto its own removal, so canDeactivate
        // and the navigation hooks are deliberately bypassed. History is replaced rather
        // than pushed so the withdrawn URL is not left in the back stack.
        const target =
          routerConfig.notFoundRoute || routerConfig.defaultRoute || '';
        try {
          window.history.replaceState(
            { params: {} },
            '',
            toBrowserPath(core.joinPaths(routerConfig.base, target)),
          );
        } catch {}
        void activateRoute(target);
      }
    };
  }

  async function activateRoute(
    path: string,
    passedParams?: Record<string, string | number | boolean>,
    options: {
      prefetchCache?: PrefetchCache;
      onActivated?: () => void;
      skipTransition?: boolean;
    } = {},
  ) {
    console.info('[Router] activateRoute called', {
      path,
      passedParams,
      skipTransition: options.skipTransition,
      caller: new Error().stack?.split('\n')[2]?.trim(),
    });
    const route = findRouteByPath(path);
    if (!route) {
      console.info('[Router] activateRoute — no matching route', {
        path,
        fallback: routerConfig.notFoundRoute || '(none)',
      });
      if (routerConfig.notFoundRoute)
        return activateRoute(routerConfig.notFoundRoute, passedParams, options);
      return;
    }

    // Prevent re-entrant activation
    if (_isActivating) {
      console.info(
        '[Router] activateRoute blocked — re-entrant call while activating',
        { path, currentPath: _lastActivatedPath },
      );
      return;
    }

    if (route.canActivate) {
      let allowed = false;
      try {
        allowed = await route.canActivate();
      } catch {
        allowed = false;
      }
      if (!allowed) {
        // A page the server rendered for a signed-in caller is taken down when
        // the browser's own guard refuses it: the refusal is the answer.
        clearServerRenderedOutlets();
        return;
      }
    }
    const match = core.matchPath(route.path, path);
    const params = normalizeParamRecord({
      ...match?.params,
      ...passedParams,
    });

    // Deduplicate: skip if same path + same params are already active
    const paramsKey = JSON.stringify(params);
    if (path === _lastActivatedPath && paramsKey === _lastActivatedParams) {
      console.info('[Router] activateRoute skipped — duplicate path+params', {
        path,
        params,
      });
      return;
    }

    const supportsViewTransition = 'startViewTransition' in document;
    const go = async () => {
      // Guard against re-entrant calls only while go() is executing.
      // This protects against signal-triggered cascades during activation,
      // but does NOT block navigations during the view-transition animation.
      if (_isActivating) {
        console.info(
          '[Router] go() blocked — re-entrant call during execution',
          { path },
        );
        return;
      }
      console.info('[Router] go() executing', { path, route: route.path });
      _isActivating = true;
      const prefetchCacheObj = options.prefetchCache;
      routerState.isLoading.set(true);
      try {
        routerState.currentRoute.set(path);
        routerState.routeParams.set(params);
        routerState.routeData.set(route);
        const dataSignal: Signal.State<unknown> = new Signal.State(null);
        let loadDataFn: RouteConfig['loadData'] | null = null;
        if (route.loadData) {
          loadDataFn = route.loadData;
          try {
            // The first visit to a page the server rendered starts from what it loaded.
            const seeded = takeLoadedData(route.path, params);
            dataSignal.set(
              seeded ? seeded.value : await route.loadData(params),
            );
          } catch (e) {
            dataSignal.set({
              error: e instanceof Error ? e.message : 'loadData error',
            });
          }
        }
        const loadDeferred = route.loadDeferred;
        if (loadDeferred) {
          try {
            const deferredPromise = loadDeferred(params);
            deferredPromise
              .then((deferred) => {
                routerState.routeDeferred = deferred;
              })
              .catch(() => {
                routerState.routeDeferred = { __error: true };
              });
          } catch {
            routerState.routeDeferred = { __error: true };
          }
        } else {
          routerState.routeDeferred = null;
        }
        routerState.routeDataSignal = dataSignal;
        routerState.routeLoadDataFunction = loadDataFn;
        let component: OutletContent | null = null;
        if (route.component || route.loadComponent || route.remote) {
          if (prefetchCacheObj?.has(path))
            component = prefetchCacheObj.get(path) ?? null;
          else component = await core.resolveComponent(route);
        }
        routerState.routeComponent.set(component);
        const newOutlets: Outlets = { '': component };
        if (route.outlets) {
          for (const [name, val] of Object.entries(route.outlets)) {
            try {
              const cacheKey = `${path}:outlet:${name}`;
              let outletComp: OutletContent | null;
              if (prefetchCacheObj?.has(cacheKey))
                outletComp = prefetchCacheObj.get(cacheKey) ?? null;
              else if (isFunction(val)) {
                try {
                  // SAFETY: every outlet is first called bare; a loader hands back its module, and a component that needs its context throws into the catch below.
                  const res = (val as OutletLoader)();
                  const awaited = isPromiseLike(res) ? await res : res;
                  outletComp = moduleDefault(awaited);
                } catch {
                  // SAFETY: an outlet that throws when called bare needs its context, so it is a component, and the outlet calls it with one.
                  outletComp = val as OutletContent;
                }
              } else outletComp = val;
              newOutlets[name] = outletComp;
            } catch {
              newOutlets[name] = null;
            }
          }
        }
        routerState.renderVersion.set(routerState.renderVersion.get() + 1);
        routerState.outlets.set(newOutlets);
        if (route.title) document.title = route.title;
        routerState.lifecycle.onInit.set(path);
        if (options.onActivated) options.onActivated();
        _lastActivatedPath = path;
        _lastActivatedParams = paramsKey;
      } finally {
        routerState.isLoading.set(false);
        _isActivating = false;
      }
    };
    if (supportsViewTransition && !options.skipTransition) {
      routerState.isTransitioning.set(true);
      try {
        // The DOM typings promise startViewTransition; a browser without view
        // transitions does not have it.
        if (document.startViewTransition && !document.hidden) {
          const transition = document.startViewTransition(async () => {
            await go();
          });
          // go() already ran inside the callback — only await the animation.
          // If the animation rejects we must NOT re-run go().
          try {
            await transition.finished;
          } catch {}
        } else {
          await go();
        }
      } finally {
        routerState.isTransitioning.set(false);
      }
    } else await go();
  }

  async function init(options: RouterInitOptions = {}) {
    const initOptions = {
      linkSelector: 'a[href]',
      onRouteChange: null,
      ...options,
    };
    core.setupOutletElements();
    if (routerConfig.enablePrefetch) {
      const { linkSelector } = initOptions;
      let hoverTimeout: ReturnType<typeof setTimeout> | null = null;
      let lastHoveredPath: string | null = null;
      document.addEventListener('mouseover', (e) => {
        // SAFETY: the pointer is over an element, which is what a mouseover is dispatched on.
        const link = (e.target as Element).closest(linkSelector);
        if (!link) return;
        const href = link.getAttribute('href');
        if (
          !href ||
          href.startsWith('http') ||
          href.startsWith('mailto:') ||
          href.startsWith('tel:')
        )
          return;
        const path = href.startsWith('/') ? href : `/${href}`;
        if (path === lastHoveredPath) return;
        lastHoveredPath = path;
        if (hoverTimeout) clearTimeout(hoverTimeout);
        hoverTimeout = setTimeout(async () => {
          const route = findRouteByPath(path);
          if (route && !prefetchCache.has(path)) {
            try {
              await prefetchSystem.prefetchRoute(path, route);
            } catch {}
          }
        }, routerConfig.prefetchDelay);
      });
      document.addEventListener('mouseout', (e) => {
        // SAFETY: the pointer left an element, which is what a mouseout is dispatched on.
        const link = (e.target as Element).closest(linkSelector);
        if (link && hoverTimeout) {
          clearTimeout(hoverTimeout);
          hoverTimeout = null;
          lastHoveredPath = null;
        }
      });
    }
    function updateActiveStates(currentPath: string) {
      const links = document.querySelectorAll(initOptions.linkSelector);
      links.forEach((link) => {
        const href = link.getAttribute('href');
        if (!href) return;
        let linkPath = href;
        if (routerConfig.base && routerConfig.base !== '/')
          linkPath = href.startsWith(routerConfig.base)
            ? href.slice(routerConfig.base.length)
            : href;
        else if (href.startsWith('/')) linkPath = href.slice(1);
        if (
          linkPath === currentPath ||
          (linkPath === '' && currentPath === routerConfig.defaultRoute)
        ) {
          link.classList.add('active');
          link.setAttribute('aria-current', 'page');
        } else {
          link.classList.remove('active');
          link.removeAttribute('aria-current');
        }
      });
    }
    window.addEventListener('popstate', async (e) => {
      let path = window.location.pathname;
      if (routerConfig.base && routerConfig.base !== '/')
        path = path.startsWith(routerConfig.base)
          ? path.slice(routerConfig.base.length)
          : path;
      else if (path.startsWith('/')) path = path.slice(1);
      if (!path) path = routerConfig.defaultRoute;
      console.info('[Router] popstate fired', {
        path,
        state: e.state,
        currentRoute: routerState.currentRoute.get(),
      });
      const stateParams = historyParams(e.state);
      const currentRouteCfg = findRouteByPath(routerState.currentRoute.get());
      if (currentRouteCfg?.canDeactivate) {
        try {
          if (
            !(await currentRouteCfg.canDeactivate({
              currentRoute: currentRouteCfg,
              nextPath: path,
              nextRoute: findRouteByPath(path),
            }))
          ) {
            const currentPath = routerState.currentRoute.get();
            if (currentPath)
              window.history.pushState(
                { params: routerState.routeParams.get() },
                '',
                toBrowserPath(core.joinPaths(routerConfig.base, currentPath)),
              );
            return;
          }
        } catch {
          const currentPath = routerState.currentRoute.get();
          if (currentPath)
            window.history.pushState(
              { params: routerState.routeParams.get() },
              '',
              toBrowserPath(core.joinPaths(routerConfig.base, currentPath)),
            );
          return;
        }
      }
      updateActiveStates(path);
      console.info('[Router] popstate → activateRoute', { path, stateParams });
      activateRoute(path, stateParams);
      if (initOptions.onRouteChange)
        initOptions.onRouteChange(path, stateParams);
    });
    document.addEventListener('click', async (e) => {
      // SAFETY: a click is dispatched on the element clicked.
      const link = (e.target as Element).closest(initOptions.linkSelector);
      if (!link) return;
      const href = link.getAttribute('href');
      if (
        !href ||
        href.startsWith('http') ||
        href.startsWith('mailto:') ||
        href.startsWith('tel:') ||
        href.startsWith('blob:') ||
        href.startsWith('data:')
      )
        return;
      e.preventDefault();
      let path = href;
      if (routerConfig.base && routerConfig.base !== '/')
        path = href.startsWith(routerConfig.base)
          ? href.slice(routerConfig.base.length)
          : href;
      else if (href.startsWith('/')) path = href.slice(1);
      if (!path) path = routerConfig.defaultRoute;
      const currentRouteCfg = findRouteByPath(routerState.currentRoute.get());
      if (currentRouteCfg?.canDeactivate) {
        try {
          if (
            !(await currentRouteCfg.canDeactivate({
              currentRoute: currentRouteCfg,
              nextPath: path,
              nextRoute: findRouteByPath(path),
            }))
          )
            return;
        } catch {
          return;
        }
      }

      const fullPath = toBrowserPath(core.joinPaths(routerConfig.base, path));
      console.info('[Router] link click intercepted', {
        href,
        path,
        fullPath,
        tagName: link.tagName,
        id: link.getAttribute('id'),
        className: link.className?.slice?.(0, 60),
      });
      window.history.pushState({ params: {} }, '', fullPath);

      updateActiveStates(path);
      activateRoute(path);
      if (initOptions.onRouteChange) initOptions.onRouteChange(path, {});
    });
    effect(() => {
      const current = routerState.currentRoute.get();
      if (current) updateActiveStates(current);
    });
    // A route that names a flag follows it. When a flag changes which route the
    // address matches, the page is activated again where the reader is: the
    // feature appears, or the address falls through to what else matches.
    // Compared with the route actually showing, so a flag that does not change
    // which route the address matches leaves the page alone.
    effect(() => {
      const path = locationPath();
      const now = findRouteByPath(path);
      Signal.subtle.untrack(() => {
        if (_isActivating) return;
        const showing = routerState.routeData.get();
        if (!showing || now === showing) return;
        // Nothing matches and the not-found page is already showing: nothing to do.
        if (
          !now &&
          routerConfig.notFoundRoute &&
          core.matchPath(showing.path, routerConfig.notFoundRoute)
        )
          return;
        _lastActivatedPath = null;
        _lastActivatedParams = null;
        void activateRoute(path);
      });
    });
    const currentPath = locationPath();
    await ensureFlatten();
    await expandLazyParentsForPath(currentPath);
    console.info('[Router] init → activateRoute', { currentPath });
    activateRoute(currentPath);
  }

  const beforeHooks = new Set<BeforeNavigateHook>();
  const afterHooks = new Set<AfterNavigateHook>();
  function addBeforeNavigateHook(h: BeforeNavigateHook) {
    beforeHooks.add(h);
    return () => beforeHooks.delete(h);
  }
  function addAfterNavigateHook(h: AfterNavigateHook) {
    afterHooks.add(h);
    return () => afterHooks.delete(h);
  }

  async function navigateTo(
    path: string,
    params: Record<string, string | number | boolean> = {},
    options: { replace?: boolean } = {},
  ) {
    console.info('[Router] navigateTo called', {
      path,
      params,
      replace: options.replace,
      caller: new Error().stack?.split('\n')[2]?.trim(),
    });
    const route = findRouteByPath(path);
    let finalPath = path;

    const hasParamPlaceholders = /:[\w]+/.test(path);
    if (hasParamPlaceholders && Object.keys(params).length > 0 && route) {
      finalPath = core.buildUrlWithParams(route.path, params);
    } else if (!hasParamPlaceholders && route) {
      const match = core.matchPath(route.path, path);
      if (match?.params) {
        Object.assign(params, match.params);
      }
    }

    const fullPath = toBrowserPath(
      core.joinPaths(routerConfig.base, finalPath),
    );
    const currentRouteCfg = findRouteByPath(routerState.currentRoute.get());

    for (const hook of beforeHooks) {
      try {
        const res = await hook({
          from: routerState.currentRoute.get() || null,
          to: finalPath,
          params,
          route,
        });
        if (res === false) return;
      } catch {
        return;
      }
    }
    if (currentRouteCfg?.canDeactivate) {
      try {
        if (
          !(await currentRouteCfg.canDeactivate({
            currentRoute: currentRouteCfg,
            nextPath: finalPath,
            nextRoute: route || null,
          }))
        )
          return;
      } catch {
        return;
      }
    }
    if (options.replace) window.history.replaceState({ params }, '', fullPath);
    else window.history.pushState({ params }, '', fullPath);
    console.info('[Router] navigateTo → activateRoute', {
      finalPath,
      params,
      fullPath,
    });
    await activateRoute(finalPath, params);
    console.info('[Router] navigateTo complete', { finalPath });

    for (const hook of afterHooks) {
      Promise.resolve(hook({ from: null, to: finalPath, params, route })).catch(
        (cause: unknown) => {
          // An after-hook must not fail the navigation that already happened,
          // but it should not fail invisibly either.
          console.error('[Router] an after-navigate hook threw:', cause);
        },
      );
    }
  }

  const routeHandlers = new Map<string, ((...args: unknown[]) => void)[]>();
  function registerHandler(
    path: string,
    handler: (...args: unknown[]) => void,
  ) {
    const list = routeHandlers.get(path) || [];
    list.push(handler);
    routeHandlers.set(path, list);
  }

  // SAFETY: each method takes the wide form of its typed signature (every path a string, every params object a record of them), and `getDeferred` rides along for callers that reach for it.
  return {
    init,
    navigateTo: (
      path: string,
      params?: Record<string, string | number | boolean>,
      options?: { replace?: boolean },
    ) => navigateTo(path, params || {}, options || {}),
    state: routerState,
    onInit: routerState.lifecycle.onInit,
    onDestroy: routerState.lifecycle.onDestroy,
    getRoutes: () => allRoutes,
    registerRoutes,
    findRouteByPath: (p: string) => findRouteByPath(p),
    getCurrentRoute: () => {
      const cp = routerState.currentRoute.get();
      return findRouteByPath(cp);
    },
    isValidRoute: (p: string) => !!findRouteByPath(p),
    reloadCurrentRouteData: async () => {},
    getDeferred: () => routerState.routeDeferred || null,
    updateParams: (
      params: Record<string, string | number | boolean>,
      options?: { replace?: boolean },
    ) => {
      const current = routerState.currentRoute.get();
      if (current) navigateTo(current, params, options);
    },
    buildUrl: (
      pattern: string,
      params?: Record<string, string | number | boolean>,
    ) => core.buildUrlWithParams(pattern, params || {}),
    updateOutlet: (name: string, content: OutletContent) => {
      const currentOutlets = routerState.outlets.get();
      routerState.outlets.set({ ...currentOutlets, [name]: content });
    },
    prefetchRoute: async (path: string) => {
      const route = findRouteByPath(path);
      if (!route) throw new Error(`Route not found: ${path}`);
      await prefetchSystem.prefetchRoute(path, route);
    },
    prefetchHighPriorityRoutes: async () => {},
    startIdlePrefetching: () => {
      strategyExecutor.setStrategy('SmartPreloading');
      strategyExecutor.executeStrategy(
        allRoutes,
        routerState.currentRoute.get(),
      );
    },
    stopIdlePrefetching: () => {
      strategyExecutor.setStrategy('NoPreloading');
    },
    setPreloadStrategy: (s: PreloadStrategySetting) => {
      strategyExecutor.setStrategy(s);
    },
    executePreloadStrategy: () => {
      strategyExecutor.executeStrategy(
        allRoutes,
        routerState.currentRoute.get(),
      );
    },
    registerHandler,
    addBeforeNavigateHook,
    addAfterNavigateHook,
    /**
     * Tear this router down.
     *
     * Disposes every outlet effect and unmounts whatever they were showing, running each
     * component's own cleanup as a navigation would. An application builds one router and
     * keeps it for the life of the page, so this is for anywhere that builds more than
     * one: a test file, or a host that removes a routed area and rebuilds it.
     *
     * Idempotent, so calling it twice is not an error.
     */
    destroy: () => {
      core.destroyOutlets();
      // The router's lifecycle signals carry the path, matching onInit above, rather
      // than a flag.
      routerState.lifecycle.onDestroy.set(routerState.currentRoute.get());
    },
  } as RouterInstance<Extract<T[number]['path'], string>>;
}
